/**
 * AI Health Companion Service
 *
 * Handles communication with the AI Health Companion backend, providing both
 * streaming and non-streaming responses. Currently uses mock implementations
 * that simulate realistic AI behavior for development and testing.
 *
 * Features:
 * - Non-streaming responses for simple interactions
 * - Streaming responses with multi-stage pipeline visualization
 * - Emergency detection and safety screening
 * - Health-context aware responses
 * - Structured sections, actions, suggestions, and citations
 * - Realistic delays and content generation
 */

import type {
  AIHealthCompanionRequest,
  AIHealthCompanionResponse,
  StreamingChunk,
  AIPipelineStage,
  AIResponseSection,
  AICitation,
  AIHealthAction,
} from '../types/ai-health-companion-types';

// Re-export for backward compatibility
export { sendAIMessage } from './ai-assistant-service';

/* ============================================================================
 * CONSTANTS
 * ========================================================================== */

/**
 * Keywords that indicate a potential emergency situation
 */
export const EMERGENCY_KEYWORDS = [
  'chest pain',
  "can't breathe",
  'cannot breathe',
  'stroke',
  'suicide',
  'kill myself',
  'overdose',
  'heart attack',
  'severe bleeding',
  'unconscious',
  'seizure',
  'anaphylaxis',
  'choking',
  'severe pain',
  'difficulty breathing',
  'confusion',
  'slurred speech',
];

/**
 * Standard safety disclaimer for medical topics
 */
const SAFETY_DISCLAIMER =
  'This information is for educational purposes only and does not replace professional medical advice. Always consult with your healthcare provider before making any decisions about your health.';

/**
 * Emergency safety disclaimer
 */
const EMERGENCY_DISCLAIMER =
  'If you are experiencing a medical emergency, please call 911 or your local emergency services immediately. Do not rely on this chat for emergency medical care.';

/* ============================================================================
 * MOCK RESPONSE TEMPLATES
 * ========================================================================== */

/**
 * Mock response templates categorized by topic
 */
const RESPONSE_TEMPLATES = {
  health: `Based on your health profile, I can provide some general guidance on this topic.

It's important to maintain a balanced approach to your health concerns. The symptoms or conditions you're describing can have various causes, and the best course of action often depends on your specific medical history, current medications, and overall health status.

I'd recommend discussing this with your healthcare provider at your next appointment. They can review your complete medical history and provide personalized recommendations. In the meantime, monitoring your symptoms and keeping a record of when they occur can be very helpful for your care team.`,

  testResults: `I can help you understand what your test results might indicate.

Test results should always be interpreted in the context of your overall health picture. While certain values may fall outside the normal range, this doesn't always indicate a serious problem. Your healthcare provider will consider your medical history, current symptoms, and other test results when evaluating these findings.

If you have specific questions about recent test results, I recommend scheduling a follow-up appointment with your provider. They can explain what the results mean for your specific situation and discuss any necessary next steps or treatment adjustments.`,

  medications: `I understand you have questions about your medications.

Medication management is an important part of your health care. It's crucial to take medications as prescribed and to be aware of potential interactions, especially if you're taking multiple medications or supplements. Never stop or change your medication dosage without consulting your healthcare provider first.

If you're experiencing side effects or have concerns about drug interactions, please contact your provider or pharmacist. They can review your complete medication list and make appropriate recommendations to ensure your safety and treatment effectiveness.`,

  appointments: `I can help you with information about your appointments and scheduling.

Regular appointments with your healthcare provider are an important part of maintaining your health. Keeping track of upcoming visits, preparing questions in advance, and following through with recommended follow-ups can help you get the most out of your care.

If you need to schedule, reschedule, or have questions about an upcoming appointment, you can use the appointments section of your patient portal or contact your provider's office directly. Make sure to arrive early and bring any relevant health information or questions you'd like to discuss.`,

  general: `Thank you for reaching out. I'm here to help you navigate your health information.

As your AI Health Companion, I can assist you with understanding your health records, medication information, appointment scheduling, and general health education. I have access to your health profile and can provide context-aware assistance based on your medical history and current health status.

Feel free to ask me about your test results, medications, upcoming appointments, or general health questions. I'll do my best to provide helpful information while ensuring you have the guidance you need to work effectively with your healthcare team.`,
};

/**
 * Mock citations database
 */
const MOCK_CITATIONS: Omit<AICitation, 'id'>[] = [
  {
    source: 'Mayo Clinic',
    title: 'Patient Care and Health Information',
    url: 'https://www.mayoclinic.org/patient-care-and-health-information',
    relevanceScore: 0.92,
  },
  {
    source: 'CDC Guidelines',
    title: 'Clinical Practice Guidelines',
    url: 'https://www.cdc.gov/guidelines',
    relevanceScore: 0.88,
  },
  {
    source: 'American Heart Association',
    title: 'Heart Health Resources',
    url: 'https://www.heart.org/health-topics',
    relevanceScore: 0.85,
  },
  {
    source: 'National Institutes of Health',
    title: 'MedlinePlus Health Topics',
    url: 'https://medlineplus.gov/healthtopics.html',
    relevanceScore: 0.90,
  },
  {
    source: 'American Diabetes Association',
    title: 'Diabetes Care Guidelines',
    url: 'https://www.diabetes.org/diabetes',
    relevanceScore: 0.87,
  },
];

/* ============================================================================
 * UTILITY FUNCTIONS
 * ========================================================================== */

/**
 * Detects if a message contains emergency keywords
 * @param message - The message to check
 * @returns True if emergency keywords are detected
 */
export function detectEmergency(message: string): boolean {
  const lowerMessage = message.toLowerCase();
  return EMERGENCY_KEYWORDS.some((keyword) => lowerMessage.includes(keyword));
}

/**
 * Determines which response template to use based on message content
 * @param message - The user's message
 * @param mode - The conversation mode
 * @returns The appropriate template key
 */
function selectResponseTemplate(
  message: string,
  mode?: string,
): keyof typeof RESPONSE_TEMPLATES {
  const lowerMessage = message.toLowerCase();

  if (mode === 'test-explanation' || lowerMessage.match(/\b(test|result|lab|blood|screening)\b/)) {
    return 'testResults';
  }

  if (mode === 'medication-review' || lowerMessage.match(/\b(medication|medicine|drug|pill|prescription)\b/)) {
    return 'medications';
  }

  if (lowerMessage.match(/\b(appointment|schedule|visit|booking)\b/)) {
    return 'appointments';
  }

  if (
    mode === 'symptom-assessment' ||
    lowerMessage.match(/\b(symptom|pain|feeling|sick|hurts|ache)\b/)
  ) {
    return 'health';
  }

  return 'general';
}

/**
 * Generates mock suggestions based on the conversation context
 * @param template - The template being used
 * @param hasHealthContext - Whether health context is available
 * @returns Array of suggestion strings
 */
function generateSuggestions(template: keyof typeof RESPONSE_TEMPLATES, hasHealthContext: boolean): string[] {
  const baseSuggestions: Record<keyof typeof RESPONSE_TEMPLATES, string[]> = {
    health: [
      'What symptoms should I watch for?',
      'When should I contact my doctor?',
      'Are there lifestyle changes that could help?',
      'What are the next steps?',
    ],
    testResults: [
      'What do these specific values mean?',
      'Should I schedule a follow-up?',
      'Are there any lifestyle changes I should make?',
      'What other tests might be needed?',
    ],
    medications: [
      'What are common side effects?',
      'Are there any food interactions?',
      'What should I do if I miss a dose?',
      'Can I take this with my other medications?',
    ],
    appointments: [
      'Show my upcoming appointments',
      'How do I prepare for my next visit?',
      'What should I bring to my appointment?',
      'Can I reschedule my appointment?',
    ],
    general: [
      'Show my recent test results',
      'What medications am I currently taking?',
      'When is my next appointment?',
      'What preventive screenings are due?',
    ],
  };

  return baseSuggestions[template].slice(0, hasHealthContext ? 4 : 3);
}

/**
 * Generates mock sections based on the template and context
 * @param template - The template being used
 * @param emergencyDetected - Whether an emergency was detected
 * @returns Array of response sections
 */
function generateSections(
  template: keyof typeof RESPONSE_TEMPLATES,
  emergencyDetected: boolean,
): AIResponseSection[] {
  if (emergencyDetected) {
    return [
      {
        type: 'warning',
        title: 'Emergency Detected',
        content:
          'Your message suggests you may be experiencing a medical emergency. Please seek immediate medical attention.',
        severity: 'critical',
      },
    ];
  }

  const sectionTemplates: Record<keyof typeof RESPONSE_TEMPLATES, AIResponseSection[]> = {
    health: [
      {
        type: 'summary',
        title: 'Health Guidance',
        content:
          'Based on your health profile, here are some general recommendations for managing your condition.',
      },
    ],
    testResults: [
      {
        type: 'test-explanation',
        title: 'Understanding Your Results',
        content: 'Test results provide valuable insights into your current health status.',
      },
      {
        type: 'recommendation',
        title: 'Next Steps',
        content:
          'Schedule a follow-up appointment to discuss these results with your healthcare provider.',
      },
    ],
    medications: [
      {
        type: 'medication-info',
        title: 'Medication Safety',
        content:
          'Always take medications as prescribed and report any side effects to your healthcare provider.',
      },
    ],
    appointments: [
      {
        type: 'detail',
        title: 'Appointment Information',
        content: 'Your upcoming appointments and scheduling options are available in your patient portal.',
      },
    ],
    general: [],
  };

  return sectionTemplates[template];
}

/**
 * Generates mock actions based on the template and emergency status
 * @param template - The template being used
 * @param emergencyDetected - Whether an emergency was detected
 * @returns Array of health actions
 */
function generateActions(
  template: keyof typeof RESPONSE_TEMPLATES,
  emergencyDetected: boolean,
): AIHealthAction[] {
  if (emergencyDetected) {
    return [
      {
        type: 'call_emergency',
        label: 'Call 911',
        payload: { emergency: true },
        priority: 'urgent',
      },
      {
        type: 'message_provider',
        label: 'Contact My Provider',
        payload: { urgent: true },
        priority: 'high',
      },
    ];
  }

  const actionTemplates: Record<keyof typeof RESPONSE_TEMPLATES, AIHealthAction[]> = {
    health: [
      {
        type: 'schedule_screening',
        label: 'Schedule Follow-up',
        payload: { appointmentType: 'follow-up' },
        priority: 'medium',
      },
      {
        type: 'track_symptom',
        label: 'Track Symptoms',
        payload: {},
        priority: 'low',
      },
    ],
    testResults: [
      {
        type: 'explain_test_result',
        label: 'View Full Results',
        payload: { navigate: '/test-results' },
        priority: 'medium',
      },
      {
        type: 'schedule_screening',
        label: 'Schedule Follow-up',
        payload: { appointmentType: 'results-discussion' },
        priority: 'medium',
      },
    ],
    medications: [
      {
        type: 'check_medication_interaction',
        label: 'Check Interactions',
        payload: {},
        priority: 'medium',
      },
      {
        type: 'navigate',
        label: 'View All Medications',
        payload: { path: '/medications' },
        priority: 'low',
      },
    ],
    appointments: [
      {
        type: 'schedule_screening',
        label: 'Book Appointment',
        payload: {},
        priority: 'medium',
      },
      {
        type: 'navigate',
        label: 'View Calendar',
        payload: { path: '/appointments' },
        priority: 'low',
      },
    ],
    general: [
      {
        type: 'navigate',
        label: 'View Dashboard',
        payload: { path: '/dashboard' },
        priority: 'low',
      },
    ],
  };

  return actionTemplates[template];
}

/**
 * Generates mock citations
 * @param count - Number of citations to generate
 * @returns Array of citations
 */
function generateCitations(count: number): AICitation[] {
  return MOCK_CITATIONS.slice(0, count).map((citation, index) => ({
    ...citation,
    id: `citation-${Date.now()}-${index}`,
  }));
}

/**
 * Simulates a realistic delay
 * @param min - Minimum delay in milliseconds
 * @param max - Maximum delay in milliseconds
 * @returns Promise that resolves after the delay
 */
function delay(min: number, max: number): Promise<void> {
  const delayMs = Math.floor(Math.random() * (max - min + 1)) + min;
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/* ============================================================================
 * PUBLIC API - NON-STREAMING
 * ========================================================================== */

/**
 * Sends a message to the AI Health Companion and receives a complete response
 * (non-streaming version)
 *
 * @param request - The AI Health Companion request payload
 * @returns Promise resolving to the complete AI response
 */
export async function sendHealthCompanionMessage(
  request: AIHealthCompanionRequest,
): Promise<AIHealthCompanionResponse> {
  // Simulate realistic processing delay
  await delay(800, 1200);

  // Check for emergency
  const emergencyDetected = detectEmergency(request.message);

  // Select appropriate response template
  const template = selectResponseTemplate(request.message, request.conversation_mode);

  // Get base response content
  let responseMessage = RESPONSE_TEMPLATES[template];

  // Customize for emergency
  if (emergencyDetected) {
    responseMessage = `⚠️ EMERGENCY DETECTED ⚠️

${EMERGENCY_DISCLAIMER}

If you are experiencing a medical emergency, please:
1. Call 911 immediately
2. Do not wait for a response from this chat
3. Seek immediate medical attention

Your safety is the top priority. Emergency services can provide the immediate help you need.`;
  }

  // Generate response components
  const sections = generateSections(template, emergencyDetected);
  const actions = generateActions(template, emergencyDetected);
  const suggestions = generateSuggestions(template, !!request.health_context);
  const citations = emergencyDetected ? [] : generateCitations(3);

  // Build complete response
  const response: AIHealthCompanionResponse = {
    message: responseMessage,
    sections,
    actions,
    suggestions,
    citations,
    emergency_detected: emergencyDetected,
    safety_disclaimer: emergencyDetected ? EMERGENCY_DISCLAIMER : SAFETY_DISCLAIMER,
    confidence_score: emergencyDetected ? 0.99 : 0.85 + Math.random() * 0.1,
    pipeline_stages_completed: [
      'understanding',
      'safety-screening',
      'context-loading',
      'analyzing',
      'generating',
      'citing',
      'complete',
    ],
    metadata: {
      model: 'claude-3-sonnet-mock',
      tokens_used: 450 + Math.floor(Math.random() * 200),
      processing_time_ms: 800 + Math.floor(Math.random() * 400),
    },
  };

  return response;
}

/* ============================================================================
 * PUBLIC API - STREAMING
 * ========================================================================== */

/**
 * Streaming callbacks interface
 */
export interface StreamingCallbacks {
  /** Called for each content/data chunk */
  onChunk: (chunk: StreamingChunk) => void;

  /** Called when pipeline stage changes */
  onStageUpdate: (stage: AIPipelineStage) => void;

  /** Called when streaming completes successfully */
  onComplete: (response: AIHealthCompanionResponse) => void;

  /** Called if an error occurs */
  onError: (error: Error) => void;
}

/**
 * Sends a message to the AI Health Companion with streaming response
 *
 * @param request - The AI Health Companion request payload
 * @param callbacks - Callback functions for streaming events
 * @returns Abort function to cancel the streaming
 */
export function streamHealthCompanionMessage(
  request: AIHealthCompanionRequest,
  callbacks: StreamingCallbacks,
): () => void {
  let aborted = false;

  // Abort function
  const abort = () => {
    aborted = true;
  };

  // Start streaming process
  (async () => {
    try {
      // Check for emergency immediately
      const emergencyDetected = detectEmergency(request.message);

      // Select appropriate response template
      const template = selectResponseTemplate(request.message, request.conversation_mode);

      // Get response content
      let responseMessage = RESPONSE_TEMPLATES[template];

      // Customize for emergency
      if (emergencyDetected) {
        responseMessage = `⚠️ EMERGENCY DETECTED ⚠️

${EMERGENCY_DISCLAIMER}

If you are experiencing a medical emergency, please:
1. Call 911 immediately
2. Do not wait for a response from this chat
3. Seek immediate medical attention

Your safety is the top priority. Emergency services can provide the immediate help you need.`;
      }

      // Generate response components
      const sections = generateSections(template, emergencyDetected);
      const actions = generateActions(template, emergencyDetected);
      const suggestions = generateSuggestions(template, !!request.health_context);
      const citations = emergencyDetected ? [] : generateCitations(3);

      // Pipeline stages with delays
      const stages: Array<{ stage: AIPipelineStage; delay: number }> = [
        { stage: 'understanding', delay: 200 },
        { stage: 'safety-screening', delay: 300 },
        { stage: 'context-loading', delay: 400 },
        { stage: 'analyzing', delay: 500 },
        { stage: 'generating', delay: 0 }, // Streaming happens here
        { stage: 'citing', delay: 200 },
        { stage: 'complete', delay: 100 },
      ];

      // Execute pipeline stages
      for (const { stage, delay: stageDelay } of stages) {
        if (aborted) return;

        // Update stage
        callbacks.onStageUpdate(stage);
        callbacks.onChunk({ type: 'stage', stage });

        // Wait for stage processing
        if (stageDelay > 0) {
          await delay(stageDelay, stageDelay + 100);
        }

        // During safety screening, check for emergency
        if (stage === 'safety-screening' && emergencyDetected) {
          callbacks.onChunk({
            type: 'content',
            content: '',
            emergency_detected: true,
          });
        }

        // During generating stage, stream the content
        if (stage === 'generating') {
          // Split message into words
          const words = responseMessage.split(' ');

          // Stream word by word
          for (let i = 0; i < words.length; i++) {
            if (aborted) return;

            const word = words[i];
            const contentChunk = i === 0 ? word : ` ${word}`;

            callbacks.onChunk({
              type: 'content',
              content: contentChunk,
            });

            // Realistic delay between words
            await delay(30, 50);
          }
        }

        // After generating, emit sections
        if (stage === 'citing' && sections.length > 0) {
          for (const section of sections) {
            if (aborted) return;
            callbacks.onChunk({
              type: 'section',
              section,
            });
            await delay(50, 100);
          }
        }

        // Emit actions
        if (stage === 'citing' && actions.length > 0) {
          for (const action of actions) {
            if (aborted) return;
            callbacks.onChunk({
              type: 'action',
              action,
            });
            await delay(50, 100);
          }
        }

        // Emit suggestions
        if (stage === 'citing' && suggestions.length > 0) {
          if (aborted) return;
          callbacks.onChunk({
            type: 'suggestion',
            suggestions,
          });
          await delay(50, 100);
        }

        // Emit citations
        if (stage === 'citing' && citations.length > 0) {
          for (const citation of citations) {
            if (aborted) return;
            callbacks.onChunk({
              type: 'citation',
              citation,
            });
            await delay(50, 100);
          }
        }
      }

      if (aborted) return;

      // Send completion chunk
      callbacks.onChunk({ type: 'done' });

      // Build complete response
      const completeResponse: AIHealthCompanionResponse = {
        message: responseMessage,
        sections,
        actions,
        suggestions,
        citations,
        emergency_detected: emergencyDetected,
        safety_disclaimer: emergencyDetected ? EMERGENCY_DISCLAIMER : SAFETY_DISCLAIMER,
        confidence_score: emergencyDetected ? 0.99 : 0.85 + Math.random() * 0.1,
        pipeline_stages_completed: stages.map((s) => s.stage),
        metadata: {
          model: 'claude-3-sonnet-mock-streaming',
          tokens_used: 450 + Math.floor(Math.random() * 200),
          processing_time_ms: stages.reduce((sum, s) => sum + s.delay, 0),
        },
      };

      // Call completion callback
      callbacks.onComplete(completeResponse);
    } catch (error) {
      if (!aborted) {
        callbacks.onError(error instanceof Error ? error : new Error('Unknown error occurred'));
      }
    }
  })();

  return abort;
}
